/**
 * Default reCAPTCHA script URL
 */ const DEFAULT_RECAPTCHA_URL = 'https://www.google.com/recaptcha/enterprise.js';
/**
 * Builds the reCAPTCHA script URL with query parameters
 */ function buildRecaptchaUrl(config) {
    const baseUrl = config.scriptUrl || DEFAULT_RECAPTCHA_URL;
    const params = new URLSearchParams();
    // Add render parameter with site key
    params.append('render', config.siteKey);
    // Add language parameter if specified
    if (config.language) {
        params.append('hl', config.language);
    }
    return `${baseUrl}?${params.toString()}`;
}
/**
 * Checks if the reCAPTCHA script is already loaded
 */ function isRecaptchaLoaded() {
    var ref;
    return typeof window !== 'undefined' && typeof ((ref = window.grecaptcha) === null || ref === void 0 ? void 0 : ref.enterprise) !== 'undefined';
}
/**
 * Checks if a reCAPTCHA script tag already exists in the document
 */ function hasRecaptchaScript() {
    if (typeof document === 'undefined') return false;
    const scripts = document.querySelectorAll('script[src*="recaptcha"]');
    return scripts.length > 0;
}
/**
 * Loads the Google reCAPTCHA v3 script dynamically
 */ function loadRecaptchaScript(config) {
    return new Promise((resolve, reject)=>{
        // Check if we're in a browser environment
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            reject(new Error('reCAPTCHA can only be loaded in a browser environment'));
            return;
        }
        // Check if reCAPTCHA is already loaded
        if (isRecaptchaLoaded()) {
            resolve();
            return;
        }
        // Check if script is already being loaded
        if (hasRecaptchaScript()) {
            // Wait for the existing script to load
            const checkInterval = setInterval(()=>{
                if (isRecaptchaLoaded()) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);
            // Timeout after 10 seconds
            setTimeout(()=>{
                clearInterval(checkInterval);
                if (!isRecaptchaLoaded()) {
                    reject(new Error('reCAPTCHA script loading timeout'));
                }
            }, 10000);
            return;
        }
        // Create and inject the script tag
        const script = document.createElement('script');
        script.src = buildRecaptchaUrl(config);
        script.async = true;
        script.defer = true;
        script.onload = ()=>{
            // Wait for grecaptcha to be available
            const checkReady = ()=>{
                if (isRecaptchaLoaded()) {
                    resolve();
                } else {
                    setTimeout(checkReady, 50);
                }
            };
            checkReady();
        };
        script.onerror = ()=>{
            reject(new Error('Failed to load reCAPTCHA script'));
        };
        // Add script to document head
        document.head.appendChild(script);
    });
}
/**
 * Removes the reCAPTCHA script from the document
 */ function removeRecaptchaScript() {
    if (typeof document === 'undefined') return;
    const scripts = document.querySelectorAll('script[src*="recaptcha"]');
    scripts.forEach((script)=>script.remove()
    );
    // Clean up the global grecaptcha object
    if (typeof window !== 'undefined' && window.grecaptcha) {
        // Use Reflect.deleteProperty to avoid TypeScript strict mode issues
        Reflect.deleteProperty(window, 'grecaptcha');
    }
}
/**
 * Waits for reCAPTCHA to be ready
 */ function waitForRecaptchaReady() {
    return new Promise((resolve, reject)=>{
        if (typeof window === 'undefined') {
            reject(new Error('reCAPTCHA can only be used in a browser environment'));
            return;
        }
        if (!isRecaptchaLoaded()) {
            reject(new Error('reCAPTCHA script is not loaded'));
            return;
        }
        window.grecaptcha.enterprise.ready(()=>{
            resolve();
        });
    });
}

export { isRecaptchaLoaded, loadRecaptchaScript, removeRecaptchaScript, waitForRecaptchaReady };
//# sourceMappingURL=recaptcha-loader.js.map
